import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { APPROVAL_EVENT } from "@/lib/approval";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [business, setBusiness] = useState(null);
  const [approval, setApproval] = useState(null);   // solo titolari: { status } (approvazione manuale)
  const [demo, setDemo] = useState(null);   // solo account dimostrativi: { is_demo, apps, active } (vedi DemoSwitcher)
  const [mfa, setMfa] = useState(null);   // solo Super Admin: { required, enabled, session_verified }
  const [loading, setLoading] = useState(true);
  // Esito dell'ultima /auth/me: "ok" | "auth" (401, sessione davvero non valida) | "transient"
  // (rete, 5xx, timeout, cold start — non significa "non loggato").
  const lastStatusRef = useRef(null);

  const fetchMe = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data.user);
      setBusiness(r.data.business);
      setMfa(r.data.mfa || null);
      setApproval(r.data.approval || null);
      setDemo(r.data.demo || null);
      lastStatusRef.current = "ok";
      return r.data;
    } catch (err) {
      // Solo un 401 esplicito significa "sessione non valida" → si torna al login.
      // Qualunque altro errore (rete, 5xx, timeout, cold start del backend) è
      // transitorio: non cancelliamo lo stato di autenticazione corrente.
      if (err?.response?.status === 401) {
        lastStatusRef.current = "auth";
        setUser(null);
        setBusiness(null);
        setMfa(null);
        setApproval(null);
        setDemo(null);
      } else {
        lastStatusRef.current = "transient";
      }
      return null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    let attempt = 0;
    const MAX_ATTEMPTS = 5;

    const tick = async () => {
      await fetchMe();
      if (cancelled) return;
      // Esito transitorio al primo caricamento: nessuna risposta definitiva
      // (né "loggato" né "sessione scaduta") → ritenta con backoff invece di
      // mostrare il login. `loading` resta true nel frattempo (ProtectedRoute
      // mostra lo spinner, non il redirect).
      if (lastStatusRef.current === "transient" && attempt < MAX_ATTEMPTS - 1) {
        attempt += 1;
        setTimeout(tick, Math.min(1000 * 2 ** attempt, 15000));
        return;
      }
      setLoading(false);
    };

    tick();
    return () => { cancelled = true; };
  }, [fetchMe]);

  // Una risposta 403 "approval_required" (p.es. sospensione con la dashboard già aperta)
  // rilegge lo stato: ProtectedRoute mostra subito la pagina di stato.
  useEffect(() => {
    const onGate = () => { fetchMe(); };
    window.addEventListener(APPROVAL_EVENT, onGate);
    return () => window.removeEventListener(APPROVAL_EVENT, onGate);
  }, [fetchMe]);

  const logout = async () => {
    // La sessione locale va chiusa comunque, anche se l'API non risponde.
    try { await api.post("/auth/logout"); } catch { /* voluto */ }
    setUser(null);
    setBusiness(null);
    setDemo(null);
    window.location.href = "/";
  };

  const role = user?.role || null;
  const canManage = role === "owner" || role === "manager";
  const canOperate = canManage || role === "staff";
  const isOwner = role === "owner";

  return (
    <AuthContext.Provider value={{ user, business, approval, mfa, demo, setUser, setBusiness, loading, refresh: fetchMe, logout, role, canManage, canOperate, isOwner }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
